import cloneDeep from 'lodash-es/cloneDeep';
import { computed, ComputedRef, Ref, ref } from 'vue';

import isDefined from '../../composables/useValidation/utils/isDefined';
import DataView from '../DataView/DataView.vue';

type DataViewInstance = InstanceType<typeof DataView>;

/**
 * Contains metadata and configuration for the filters.
 * @see https://www.typescriptlang.org/docs/handbook/2/mapped-types.html
 */
export type UseFiltersSchema<Values extends object, Groups extends string> = {
  [Property in keyof Values]: {
    defaultValue?: Values[Property];
    group?: Groups;
    isActive?: (value: Values[Property]) => boolean;
  };
};

export interface UseFiltersArgs<Values extends object, Groups extends string> {
  schema: UseFiltersSchema<Values, Groups>;
  /** A ref for an instance of DataView */
  dataViewRef: Ref<unknown>; // Note: using `Ref<InstanceType<typeof DataView>>` here causes type errors when providing a value for this argument
}

export interface UseFiltersReturnType<Values = object, Groups extends string = string> {
  applyFilters: () => void;
  resetAllFilters: () => void;
  resetFilterGroup: (group: string) => void; // Note: group is intentionally not typed as `Groups` since there is no way to pass in a Groups type to UseFiltersReturnType within DataViewFilters.vue
  undoWorkingFilters: () => void;
  activeFiltersCounts: ComputedRef<Record<Groups, number>>;
  totalActiveFiltersCount: ComputedRef<number>;
  appliedFilters: Ref<Values>;
  workingFilters: Ref<Values>;
}

/**
 * Provides utility functions for working with `DataViewFilters`.
 */
export function useFilters<Values extends object, Groups extends string>({
  schema,
  dataViewRef,
}: UseFiltersArgs<Values, Groups>): UseFiltersReturnType<Values, Groups> {
  const appliedFilters = ref({}) as Ref<Values>;
  const workingFilters = ref({}) as Ref<Values>;

  for (const filterName in schema) {
    // @ts-expect-error "could be instantiated with an arbitrary type"; TODO: figure out how to resolve the types
    appliedFilters.value[filterName] = schema[filterName].defaultValue;
    // @ts-expect-error "could be instantiated with an arbitrary type"; TODO: figure out how to resolve the types
    workingFilters.value[filterName] = schema[filterName].defaultValue;
  }

  const dvRef = dataViewRef as Ref<DataViewInstance>;

  /**
   * For when an "Apply" button is clicked. It does the following:
   *   1) applies the working filter state
   *   2) sets the current page to 1
   *   3) emits the "update" event from DataView
   */
  function applyFilters() {
    appliedFilters.value = cloneDeep(workingFilters.value);
    dvRef.value.updateCurrentFilters(appliedFilters.value, { shouldEmit: true });
  }

  /**
   * For when a "Reset all" button is clicked. It does the following:
   *   1) applies the defaultValue filter values to all filters
   *   2) sets the current page to 1
   *   3) emits the "update" event from DataView
   */
  function resetAllFilters() {
    for (const filterName in schema) {
      // @ts-expect-error "could be instantiated with an arbitrary type"; TODO: figure out how to resolve the types
      appliedFilters.value[filterName] = schema[filterName].defaultValue;
      // @ts-expect-error "could be instantiated with an arbitrary type"; TODO: figure out how to resolve the types
      workingFilters.value[filterName] = schema[filterName].defaultValue;
    }

    dvRef.value.updateCurrentFilters(appliedFilters.value, { shouldEmit: true });
  }

  /**
   * For when a "Reset" button is clicked. It does the following:
   *   1) applies the defaultValue filter values to the given filter group
   *   2) sets the current page to 1
   *   3) emits the "update" event from DataView
   */
  function resetFilterGroup(group: Groups) {
    for (const filterName in schema) {
      if (schema[filterName].group === group) {
        // @ts-expect-error "could be instantiated with an arbitrary type"; TODO: figure out how to resolve the types
        appliedFilters.value[filterName] = schema[filterName].defaultValue;
        // @ts-expect-error "could be instantiated with an arbitrary type"; TODO: figure out how to resolve the types
        workingFilters.value[filterName] = schema[filterName].defaultValue;
      }
    }

    dvRef.value.updateCurrentFilters(appliedFilters.value, { shouldEmit: true });
  }

  /**
   * Resets the `workingFilters` to match the `appliedFilters`. This can be used when the FilterDrawer or a FilterDropdown is dismissed without clicking "Reset" or "Apply".
   */
  function undoWorkingFilters() {
    workingFilters.value = cloneDeep(appliedFilters.value);
  }

  const activeFiltersCounts = computed(() => {
    const counts = {} as Record<Groups, number>;

    for (const filterName in schema) {
      const config = schema[filterName];
      const value = appliedFilters.value[filterName];
      const isActive = config.isActive?.(value) || isDefined(value);

      if (isActive && config.group) {
        counts[config.group] = (counts[config.group] ?? 0) + 1;
      }
    }

    return counts;
  });

  const totalActiveFiltersCount = computed(() => {
    let count = 0;

    for (const filterName in schema) {
      const config = schema[filterName];
      const value = appliedFilters.value[filterName];
      const isActive = config.isActive?.(value) || isDefined(value);

      if (isActive) {
        count += 1;
      }
    }

    return count;
  });

  return {
    applyFilters,
    resetAllFilters,
    // @ts-expect-error "could be instantiated with a different subtype": TODO: figure out how to resolve the types
    resetFilterGroup,
    undoWorkingFilters,
    activeFiltersCounts,
    totalActiveFiltersCount,
    appliedFilters,
    workingFilters,
  };
}

export default useFilters;
